import { gsap } from '@gsap'
import { MEDIA } from '@spot'
import { useEffect, useLayoutEffect, useMemo } from 'react'
import { useIsomorphicLayoutEffect } from 'usehooks-ts'
import usePrefersReducedMotion from '../use-prefers-reduced-motion'

interface UseGSAPContext {
  type?: 'effect' | 'layout' | 'isomorphic'
  scope?: any
  callback: (_c?: gsap.Context) => void
  deps?: any[]
  cleanup?: () => void
  isEnabled?: boolean
  respectReducedMotion?: boolean
}

interface UseGSAPMedia {
  media: any
  type?: 'effect' | 'layout' | 'isomorphic'
  scope?: any
  callback: (_c?: gsap.Context) => void
  deps?: any[]
  isEnabled?: boolean
  respectReducedMotion?: boolean
}

const useGSAPContext = ({
  callback,
  scope,
  type = 'effect',
  deps = [],
  cleanup,
  isEnabled = true,
  respectReducedMotion = true
}: UseGSAPContext) => {
  const ctx = useMemo(() => gsap.context(() => {}), [])
  const prefersReducedMotion = usePrefersReducedMotion()
  const shouldRun =
    isEnabled && (!respectReducedMotion || !prefersReducedMotion)
  const effectHook =
    type === 'effect'
      ? useEffect
      : type === 'layout'
        ? useLayoutEffect
        : useIsomorphicLayoutEffect
  effectHook(() => {
    // Always run callback so components can handle their own logic
    // But only when enabled
    if (!isEnabled) {
      return
    }

    ctx.add(() => {
      callback()
    }, scope)

    return () => {
      if (cleanup) {
        cleanup()
      }
      ctx.revert()
    }
  }, [...deps, shouldRun])
}

const useGSAPMedia = ({
  callback,
  scope,
  type = 'effect',
  deps = [],
  media = MEDIA,
  isEnabled = true,
  respectReducedMotion = true
}: UseGSAPMedia) => {
  const prefersReducedMotion = usePrefersReducedMotion()
  const shouldRun =
    isEnabled && (!respectReducedMotion || !prefersReducedMotion)
  const effectHook =
    type === 'effect'
      ? useEffect
      : type === 'layout'
        ? useLayoutEffect
        : useIsomorphicLayoutEffect

  effectHook(() => {
    if (!isEnabled) {
      return
    }

    const ctx = gsap.matchMedia()
    ctx.add(
      media,
      (c) => {
        callback(c)
      },
      scope
    )
    return () => ctx.revert()
  }, [...deps, shouldRun])
}

export { useGSAPContext, useGSAPMedia }
